// Ultime news, connected: the visit (POST /me/visit, once per opening of the
// area) gives the `since` that splits «News dall'ultimo accesso» from «News
// precedenti»; each list is an infinite query on GET /feed, asked only after
// the visit answered, with the filter of ?sources= (none: no call). When the
// filter names sources of sittings (Plenaria, Commissioni), their chamber's
// sittings stand in for the cards of those documents (SittingLists), above
// the lists of news of the other sources; with only sources of sittings there
// is no list of news. A row of a sitting in a list (kind "sitting": one row
// per sitting, whatever the filter, D7) is a card without a bookmark that
// leads to the sitting's page. The components only see NewsItems and the
// status rows.
import { useMemo } from "react";
import { useSearchParams } from "react-router";

import type { NewsItem } from "../../data/mock";
import { PanelStatus } from "../../components/PanelStatus";
import { NewsSection } from "../../components/news/NewsSection";
import { romeDay } from "../../lib/dates";
import { sittingScope, toFeedNewsItem } from "../parliament/adapt";
import { SittingLists } from "../parliament/SittingLists";
import { ExpandedNewsCard } from "./ExpandedNewsCard";
import { lastAccessLabel, toSourceTree } from "./adapt";
import { FAVOURITES_SCOPE, useFeed, useSources, useVisit, type FeedBucket, type FeedScope } from "./api";
import { useBookmarks } from "./bookmarks";
import { ListLoading, LoadMore, MORE_FAILED, REFRESH_FAILED } from "./ListStatus";
import { readNewsParams } from "./params";

const TITLE: Record<FeedBucket, string> = { new: "News dall’ultimo accesso", previous: "News precedenti" };
const EMPTY: Record<FeedBucket, string> = {
  new: "Nessuna news nuova dalle fonti selezionate.",
  previous: "Nessuna news precedente dalle fonti selezionate.",
};
/** The empty state of a list of Il mio feed: the feed's sources, not a selection. */
const EMPTY_FEED: Record<FeedBucket, string> = {
  new: "Nessuna news nuova dalle fonti del feed.",
  previous: "Nessuna news precedente dalle fonti del feed.",
};
/** The empty state of a list of the folder «Preferiti»: the pinned sources. */
const EMPTY_FAVOURITES: Record<FeedBucket, string> = {
  new: "Nessuna news nuova dalle fonti preferite.",
  previous: "Nessuna news precedente dalle fonti preferite.",
};
const FAILED = "News non disponibili.";
const NO_SOURCES = "Nessuna fonte selezionata.";

/** What stands before the lists: the visit, and a filter that names at least one source. */
type Gate = { kind: "ready" } | { kind: "loading" } | { kind: "error"; retry: () => void } | { kind: "no-sources" };

/**
 * Ultime news. Without `scope`, the filter comes from the query string
 * (?sources=: the checkboxes, a clicked node or chip): the sittings of the
 * sources of sittings, then the two lists of news of the other sources. With
 * a `scope`, Il mio feed: the lists are the feed's sources (GET
 * /feed?feed_id=) or the favourites (GET /feed?favourites=true), whatever
 * the query string says.
 */
export function LatestNews({ scope = null }: { scope?: FeedScope } = {}) {
  const [search] = useSearchParams();
  const params = readNewsParams(search);
  const scoped = scope !== null;
  const { lists, news } = useMemo(() => sittingScope(scoped ? null : params.sources), [scoped, params.sources]);
  // only sources of sittings: no list of news, so no visit either
  const sittingsOnly = lists.length > 0 && news !== null && news.length === 0;
  return (
    <>
      {lists.length > 0 && <SittingLists lists={lists} />}
      {!sittingsOnly && <NewsLists filter={news} scope={scope} search={search} />}
    </>
  );
}

/** The two lists of news; `filter` null: every source; []: none (?sources=none). */
function NewsLists({ filter, scope, search }: { filter: string[] | null; scope: FeedScope; search: URLSearchParams }) {
  const scoped = scope !== null;
  const visit = useVisit();
  const catalogue = useSources();
  const tree = useMemo(() => (catalogue.data ? toSourceTree(catalogue.data) : null), [catalogue.data]);

  // a failed call being retried («Riprova») is loading again, not still failed
  const failed = (q: { isError: boolean; isFetching: boolean }) => q.isError && !q.isFetching;
  let gate: Gate;
  if (failed(visit)) gate = { kind: "error", retry: () => void visit.refetch() };
  else if (!visit.isSuccess) gate = { kind: "loading" };
  else if (filter !== null && filter.length === 0) gate = { kind: "no-sources" };
  else gate = { kind: "ready" };

  const ready = gate.kind === "ready";
  const fresh = useFeed("new", filter, scope, ready);
  const previous = useFeed("previous", filter, scope, ready);
  const bookmarks = useBookmarks();
  const empty = scope === FAVOURITES_SCOPE ? EMPTY_FAVOURITES : scoped ? EMPTY_FEED : EMPTY;

  return (
    <>
      {bookmarks.failure && (
        <PanelStatus role="alert" indent={false} onRetry={bookmarks.failure.onRetry}>
          {bookmarks.failure.message}
        </PanelStatus>
      )}
      <FeedList
        bucket="new"
        dateLabel={visit.data ? lastAccessLabel(visit.data) : undefined}
        empty={empty.new}
        gate={gate}
        query={fresh}
        tree={tree}
        search={search}
        onToggleSaved={bookmarks.toggle}
      />
      <FeedList
        bucket="previous"
        empty={empty.previous}
        gate={gate}
        query={previous}
        tree={tree}
        search={search}
        onToggleSaved={bookmarks.toggle}
      />
    </>
  );
}

type FeedListProps = {
  bucket: FeedBucket;
  dateLabel?: string;
  /** What the list says when it has no card. */
  empty: string;
  gate: Gate;
  query: ReturnType<typeof useFeed>;
  tree: ReturnType<typeof toSourceTree> | null;
  search: URLSearchParams;
  onToggleSaved: (news: NewsItem) => void;
};

function FeedList({ bucket, dateLabel, empty, gate, query, tree, search, onToggleSaved }: FeedListProps) {
  // the rows of the sittings read their state against today (a sitting convoked for a day that passed)
  const today = romeDay();
  const items = useMemo(
    () =>
      (query.data?.pages.flatMap((page) => page.items) ?? []).map((row) => toFeedNewsItem(row, today, tree, search)),
    [query.data, today, tree, search],
  );
  const ready = gate.kind === "ready";
  // the cards of the last filter stand in while the new ones load: the list is ready, not loading
  const hasCards = query.data !== undefined;

  let status: React.ReactNode = null;
  if (gate.kind === "error") {
    status = (
      <PanelStatus role="alert" indent={false} onRetry={gate.retry}>
        {FAILED}
      </PanelStatus>
    );
  } else if (gate.kind === "no-sources") {
    status = <PanelStatus indent={false}>{NO_SOURCES}</PanelStatus>;
  } else if (!ready || (!hasCards && (query.isPending || query.isFetching))) {
    status = <ListLoading />;
  } else if (!hasCards && query.isError) {
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
        {FAILED}
      </PanelStatus>
    );
  } else if (query.isFetchNextPageError) {
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.fetchNextPage()}>
        {MORE_FAILED}
      </PanelStatus>
    );
  } else if (query.isError && !query.isFetching) {
    // a refetch (a new filter, an invalidation) failed while the cards of the last answer stay on the page
    status = (
      <PanelStatus role="alert" indent={false} onRetry={() => void query.refetch()}>
        {REFRESH_FAILED}
      </PanelStatus>
    );
  } else if (items.length === 0) {
    // an empty stand-in of the last filter says nothing about the new one
    status = query.isPlaceholderData ? <ListLoading /> : <PanelStatus indent={false}>{empty}</PanelStatus>;
  } else if (query.hasNextPage) {
    status = (
      <LoadMore
        isDisabled={query.isFetchingNextPage || query.isPlaceholderData}
        onPress={() => void query.fetchNextPage()}
      />
    );
  }

  return (
    <NewsSection
      title={TITLE[bucket]}
      dateLabel={dateLabel}
      items={ready && hasCards ? items : []}
      onToggleSaved={onToggleSaved}
      card={ExpandedNewsCard}
    >
      {status}
    </NewsSection>
  );
}
